// src/components/dialog/DlgConsumptionExport.tsx

import {
  DownloadTwoTone,
  ExpandMoreTwoTone,
  MemoryTwoTone,
} from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { CSVLink } from 'react-csv'

import { FolderId, FolderImage, FolderName, FolderSingle } from '@/types/domain'
import { KARMA_BasicDialog } from '@/types/karma/dialog'

import { sensorType, sensorUnit } from '../../api'
import {
  useFolder,
  useFolderImage,
  useFolderName,
  useOperatorApi,
  usePricePool,
  useRoomsMode,
} from '../../hooks'
import { exportToExcel } from '../../pages/Folder/PerformanceDetail.export'

import { DlgCloseButton } from './DlgCloseButton'

const granularityTitle = g =>
  g === 'd'
    ? 'Stündlich'
    : g === 'm'
      ? 'Täglich'
      : g === 'y'
        ? 'Monatlich'
        : g === 'yy'
          ? 'Jährlich'
          : g

const COST_BY_AREA = 'Kosten / Fläche (€)'

type Props = KARMA_BasicDialog & {
  exportAsJPEG: () => void
  exportAsPNG: () => void
  id: FolderId
  options: any
}

export const DlgConsumptionExport = ({
  exportAsJPEG,
  exportAsPNG,
  id,
  onClose,
  open,
  options,
}: Props) => {
  const { data, fileName, granularity, kpiLabel, medium, timespan } =
    options || {}

  const fData: FolderSingle = useFolder(id)
  const fName: FolderName = useFolderName(id)
  const fImage: FolderImage = useFolderImage(id)
  const roomsMode = useRoomsMode(id)
  const pricePool = usePricePool()
  const { getViewOptions } = useOperatorApi()
  const unit = sensorUnit(medium)

  const [viewCols, setViewCols] = useState(options?.viewCols || null)
  const [unitCfg, setUnitCfg] = useState(options?.unitConfig || null)

  const [accExp, setAccExp] = useState(false)

  const sheetNames = Object.keys(data).filter(f => f !== 'Gesamtverbrauch')

  useEffect(() => {
    if (viewCols && unitCfg) return
    ;(async () => {
      const data = await getViewOptions(id)
      setViewCols(data?.columns || data || {})
      setUnitCfg({ header: data?.header ?? true, values: data?.values ?? true })
    })()
  }, [getViewOptions, id, viewCols, unitCfg])

  const onExport = async () => {
    await exportToExcel(
      data,
      `${fileName}.xlsx`,
      pricePool,
      fData,
      roomsMode,
      medium,
      granularity,
      viewCols,
      unitCfg
    )
    onClose()
  }

  return (
    <Dialog
      fullWidth
      open={open}
      slotProps={{
        backdrop: { style: { backgroundColor: 'rgba(0, 0, 0, 0.66)' } },
      }}
    >
      <DialogTitle>
        <Typography sx={{ mr: 1 }}>Exportieren</Typography>

        <DlgCloseButton onClick={onClose} />
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <List sx={{ py: 0 }}>
          <ListItem>
            <ListItemIcon>
              <Avatar src={fImage} />
            </ListItemIcon>

            <ListItemText primary={'Gebäude'} secondary={fName} />
          </ListItem>

          <Divider />

          <ListItem>
            <ListItemText primary={'Zeitraum'} secondary={timespan} />
            <ListItemText primary={'Medium'} secondary={sensorType(medium)} />
            <ListItemText primary={'Einheit'} secondary={kpiLabel} />
            <ListItemText
              primary={'Granularität'}
              secondary={granularityTitle(granularity)}
            />
          </ListItem>
        </List>

        <Accordion expanded={accExp}>
          <AccordionSummary
            expandIcon={<ExpandMoreTwoTone />}
            onClick={() => setAccExp(prev => !prev)}
          >
            <Typography variant={'subtitle1'}>Messpunkte</Typography>
            <Chip label={sheetNames.length} size={'small'} sx={{ ml: 1 }} />
          </AccordionSummary>

          <AccordionDetails>
            <List sx={{ width: 1, maxHeight: 200, overflow: 'auto', py: 0 }}>
              {sheetNames.map(item => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <MemoryTwoTone />
                  </ListItemIcon>

                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions>
        <Button onClick={exportAsPNG}>
          <DownloadTwoTone />
          PNG
        </Button>
        <Button onClick={exportAsJPEG}>
          <DownloadTwoTone />
          JPEG
        </Button>
        <Divider />
        <CSVLink
          data={data['Gesamtverbrauch'] ? data['Gesamtverbrauch'] : []}
          filename={`${fileName}.csv`}
          headers={[
            'Datum',
            `Verbrauch (${unit})`,
            'Trend',
            `Verbrauch / m² (${unit})`,
            `Verbrauch / ${roomsMode} (${unit} / MA)`,
            'Kosten (€)',
            COST_BY_AREA,
            COST_BY_AREA,
            COST_BY_AREA,
            timespan,
          ]}
          target={'_blank'}
        >
          <Button>
            <DownloadTwoTone />
            CSV
          </Button>
        </CSVLink>
        <Button onClick={onExport}>
          <DownloadTwoTone />
          Excel
        </Button>
      </DialogActions>
    </Dialog>
  )
}
